import React, { useEffect, useState } from 'react';
import { SubscriptionOverlay } from '../../../components/SubscriptionOverlay';
import { getRecentlyPaired } from '../models/recentlyPaired';

type Domain = 'curriculum' | 'training';

type NewPurchasesOverlayProps = {
  domain: Domain;
  items?: ReturnType<typeof getRecentlyPaired>;
  showOverlay?: boolean;
  onClosed?: () => void;
};

const instructions: Record<Domain, { title: string; content: React.ReactNode }[]> = {
  curriculum: [
    {
      title: 'Distribute',
      content: (
        <>
          As an Admin, you can distribute Bible Study materials by <b>adding</b> leaders and learners to a{' '}
          <b>collection</b>. A collection includes all of the documents and media that people will use to study each
          session. As soon as you add someone to a collection, <i>all</i> of the available current and future sessions
          for that collection will immediately appear when they log in. “This week’s session” will be highlighted so
          they know exactly what to study.
        </>
      ),
    },
    {
      title: 'Schedule',
      content: (
        <>
          You can <b>reschedule</b> each session to use it on a different day, or remove it from the calendar
          completely. Scheduled session dates apply to everyone who is distributed the curriculum.
        </>
      ),
    },
    {
      title: 'Manage',
      content: (
        <>
          Need to add another admin for your church? Use the <b>Roles</b> feature under the “Organization” menu. From
          there, you can add people to the <b>Admin</b> role so that they can distribute and schedule the curriculum
          you’ve purchased.
          <p>
            For more information, visit our curriculum FAQs on{' '}
            <a
              href="https://support.ministrygrid.com/en/collections/2590229-digital-curriculum-on-ministry-grid"
              target="_blank"
              rel="noreferrer"
            >
              support.ministrygrid.com
            </a>
          </p>
        </>
      ),
    },
  ],
  training: [
    {
      title: 'Browse Our Catalog',
      content: <>Discover over 700+ courses and pathways of training across dozens of ministry areas.</>,
    },
    {
      title: 'Train Your Church',
      content: (
        <>
          When you find the right training, use the Easy Link or Email options on the overview page to assign that
          training to others.
        </>
      ),
    },
    {
      title: 'Customize Your Training',
      content: (
        <>
          Before assigning training, use the Customize button on any course or pathway to make a version specific for
          your church. Add or remove content from the training before assigning it out to others.
        </>
      ),
    },
  ],
};

export function NewPurchasesOverlay({ domain, items, showOverlay = false, onClosed }: NewPurchasesOverlayProps) {
  const [isVisible, setIsVisible] = useState<boolean | undefined>(undefined);
  const dismiss = () => {
    setIsVisible(false);
    if (onClosed) onClosed();
  };

  useEffect(() => {
    if (isVisible === undefined) return setIsVisible(!!items?.length || showOverlay);
    setIsVisible(showOverlay);
  }, [showOverlay]);

  if (!isVisible) return null;

  if (!items?.length && domain === 'curriculum')
    return (
      <SubscriptionOverlay
        title="Digital Curriculum"
        heading="How do digital curriculum subscriptions work?"
        subscriptions={[]}
        instructions={instructions['curriculum']}
        dismissHandler={dismiss}
      />
    );

  return (
    <SubscriptionOverlay
      title="New Purchases"
      heading="Your church is now connected to the following Ministry Grid subscriptions:"
      subscriptions={
        items?.map(({ name, subtitle, brandCode }) => ({
          name,
          subtitle,
          brandCode,
        })) || []
      }
      subscriptionOrientation="vertical"
      instructions={instructions[domain]}
      dismissHandler={dismiss}
    />
  );
}
